import { BN } from "@project-serum/anchor";
import { Governance, ProgramAccount } from "@solana/spl-governance";
import { PublicKey } from "@solana/web3.js";

import {
  ClubSortType,
  ClubType,
  FilterType,
  InvitationStatus,
  SummaryTabIdentifiers,
  SummaryTabNames,
  TreasuryFundraiseStatus,
} from "../enums/club.enum";
import { ApplicationStatus } from "../enums/clubs-wizard.enum";
import { ClubAction, TradedRightType, UserRole } from "../enums/common.enums";

import { IDetailNftData, IFormField, IOption } from "./common.interface";
import { IUserData } from "./user.interface";

export interface IBasicClubData {
  address: string;
  realmAddress: string;
  name: string;
  imageUrl: string;
  applicationStatus: ApplicationStatus;
  createdAt: Date;
  totalFundraisedAmount: number;
  numberOfMembers: number;
  numberOfNFTs: number;
  clubType: ClubType;
  roleConfig: RoleConfig[];
  tags: IOption[];
  communityMintAddress: string;
  activeFundraise: boolean;
  owner?: PublicKey;
}

export interface ClubApplication {
  profileImageUrl: string;
  pubkey: string;
  twitter: string;
  discord: string;
  telegram: string;
  linkedin: string;
  reddit: string;
  instagram: string;
  tiktok: string;
  motivationLetter: string;
  benefits: string[];
  name?: string;
  applicationId: string;
  depositAmount?: number;
}

export interface IClubMember {
  profileImageUrl: string;
  pubkey: string;
  lastSeen: string;
  twitter: string;
  discord: string;
  telegram: string;
  linkedin: string;
  reddit: string;
  instagram: string;
  tiktok: string;
  role?: string;
  name?: string;
  joined?: string;
}

export interface IClubMemberAndInvitation {
  pubkey: string;
  role: string;
  name?: string;
  imageUrl?: string;
  onlyInvited: boolean;
}

export interface ISolanaClubMember {
  pubkey: PublicKey;
  role: string;
  isAccepted?: boolean;
}

export interface IClubMemberBasicInfo {
  pubkey: PublicKey;
  role: string;
}

export interface IClubBasicInfoDto {
  address: string;
  realmAddress: string;
  communityMintAddress: string;
  name: string;
  owner: string;
  description: string;
  imageUrl: string;
  email: string;
  applicationStatus: string;
  twitter: string;
  discord: string;
  telegram: string;
  linkedin: string;
  reddit: string;
  instagram: string;
  tiktok: string;
  tags: IOption[];
}

interface IKYCConfig {
  required: boolean;
  location: number;
}

export interface IClubBasicInfo {
  address: string;
  name: string;
  type: ClubType;
  description: string;
  owner: string;
  imageUrl: string;
  email: string;
  applicationStatus: string;
  twitter: string;
  discord: string;
  telegram: string;
  linkedin: string;
  reddit: string;
  instagram: string;
  tiktok: string;
  tags: IOption[];
  roleConfig: RoleConfig[];
  numberOfMembers: number;
  communityMint: PublicKey;
  governances: ProgramAccount<Governance>[];
  kycConfig?: IKYCConfig;
}

export interface IVault {
  nfts: IDetailNftData[];
  //TODO@goran:remove question mark
  vaultAddress?: PublicKey;
}

export interface IClubDataSolana {
  title: string;
  clubAddress: PublicKey | string; //TODO: check
  ownerAddress: PublicKey;
  clubType: ClubType;
  stakeCount?: number;
  clubMint?: PublicKey;
  roleConfig?: RoleConfig[];
  numberOfMembers: number;
  governanceAccounts?: ProgramAccount<Governance>[];
  kycConfig?: IKYCConfig;
}

export interface RoleConfig {
  name: string;
  roleWeight: number;
  actions: ClubAction[];
}

export interface ISellPermissionConfig {
  from: number;
  to: number;
  votingQuorum: number;
  governance?: PublicKey;
}

export interface ISellPermissionConfigSolana {
  from: BN;
  to: BN;
  quorumMinimum: number;
  decimal: number;
  governance?: PublicKey;
}

export interface GetUserDetailsDto {
  userAddresses: string[];
}

export interface IInvitationSolana {
  status: InvitationStatus;
  memberPubKey: PublicKey;
  role: UserRole;
}

export interface ISummaryTab {
  icon: any;
  title: SummaryTabNames;
  id: SummaryTabIdentifiers;
}

export interface ITreasuryTokenAccInfo {
  tokenAddress: PublicKey | undefined;
  mint: PublicKey;
  name: string;
  image: string;
  balance: number;
  symbol: string;
  decimals: number;
  profit: number;
}

export interface IClubTreasury {
  fundraiseCount: number;
  governance: PublicKey;
  treasury: PublicKey;
  hasActiveFundraise: boolean;
  fundraiseAccounts: IFundraise[];
  treasuryAmount: number;
  treasuryDataAddress: PublicKey;
  treasuryFundraiseStatus: TreasuryFundraiseStatus;
  totalFundraised: number;
  totalFinancialPower: number;
  sellPermissions: ISellPermissionConfig[];
  profitAmount: number;
  withdrawalGovernance?: PublicKey;
  transferGovernance?: PublicKey;
  changeApprovalConfigGovernance?: PublicKey;
  tokenAccounts: ITreasuryTokenAccInfo[];
  denominatedTokenInfo?: ITreasuryTokenAccInfo;
  reservedRights?: IReservedRights;
}

export interface IFundraise {
  isActive: boolean;
  capAmount: number;
  raisedAmount: number;
  authority: PublicKey;
  fundraiseConfigAddress: PublicKey;
  allocation?: IAllocation;
}

export interface IMembersRights {
  votingPower?: number;
  ownership: number;
  financialPower: number;
  depositedAmount?: number;
  depositRecords: IDepositRecord[];
}
export interface ISortItem {
  title: string;
  type: ClubSortType;
}

export interface IDepositRecord {
  accumulatedAmount: number;
  fundraiseIndex: number;
  depositedAt: number;
  currentFundraiseDeposit: number;
  tradedRight?: ITradedRight[];
}

export interface ITradedRight {
  amount: number;
  timestamp: number;
  tradedType: TradedRightType;
}

export interface IClubFilter {
  label: string;
  value: number | string | boolean;
  filterType: FilterType;
}

export interface IClubModifiers {
  clubType: ClubType[];
  activeFundraise: boolean[];
  applicationStatus: ApplicationStatus[];
  tags: IOption[];
  searchParam: string;
  sortType: ISortItem;
}

export class TransactionError extends Error {
  public txid: string;
  constructor(message: string, txid: string) {
    super(message);
    this.txid = txid;
  }
}

export interface IWithdrawalMembersInfo {
  proposal: PublicKey;
  withdrawAmount: number;
  withdrawal: PublicKey;
  withdrawalData: PublicKey;
  hasUserClaimed: boolean;
  withdrawalMint: PublicKey;
  withdrawalMintDecimals: number;
  withdrawalMintSymbol: string;
}

export interface IClubDataForProposal {
  clubName: string;
  clubType: ClubType;
  treasury: PublicKey;
  treasuryTokens: ITreasuryTokenAccInfo[];
  denominatedTokenInfo: ITreasuryTokenAccInfo | undefined;
  userRole: string;
  roleConfig: RoleConfig[];
  mint: PublicKey;
  fundraiseCount: number;
  owner: string;
  address: string;
  governances?: ProgramAccount<Governance>[];
  memberRights: IMembersRights;
  isDashboardProposal?: boolean;
  stakeCount?: number;
  realmAddress?: string;
  clubImageUrl?: string;
  numberOfMembers?: number;
  withdrawalGovernance?: PublicKey;
  reservedRights: IReservedRights | undefined;
}

export interface IUpdateClubFormFields {
  clubName: IFormField;
  clubImage: IFormField;
  imageUpdated: IFormField;
  removeImage: IFormField;
  clubDescription: IFormField;
  clubEmail: IFormField;
  clubTags: IFormField;
  clubApplicationStatus: IFormField;
  twitter: IFormField;
  discord: IFormField;
  telegram: IFormField;
  linkedin: IFormField;
  reddit: IFormField;
  instagram: IFormField;
  tiktok: IFormField;
}

export interface IUpdateClubFormModel {
  formId: string;
  formFields: IUpdateClubFormFields;
}

export interface IUpdateClubFormValues {
  clubName: string;
  clubEmail: string;
  clubImage: string;
  imageUpdated: boolean;
  removeImage: boolean;
  clubDescription: string;
  clubTags: IOption[];
  clubApplicationStatus: string;
  twitter: string;
  discord: string;
  telegram: string;
  linkedin: string;
  reddit: string;
  instagram: string;
  tiktok: string;
  depositAmount?: number;
}

export interface IUpdateClubDto {
  signedMessage: string;
  description: string;
  imageBase64: string;
  removeImage: boolean;
  email: string;
  applicationStatus: string;
  tags: IOption[];
  twitter: string;
  discord: string;
  telegram: string;
  linkedin: string;
  reddit: string;
  instagram: string;
  tiktok: string;
  depositAmount?: number;
}
export interface IClubMemberDictionary {
  [key: string]: IClubMemberAndInvitation;
}

export interface IAllocation {
  equal?: number;
  custom?: ICustomAllocation[];
}
export interface IReservedRights {
  totalReservedRights: number;
  totalReservedPercentage: number;
  individualRights: IIndividualRights[];
}

export interface IIndividualRights {
  memberPubkey: PublicKey;
  rightPercentage: number;
  amountOfRights: number;
}

export interface ICustomAllocation {
  authority: PublicKey;
  amount: number;
}

export interface IApplicationWithDeposit {
  user: IUserData;
  depositAmount: number;
}

//Interfaces for instructions
export interface ISolanaCustomAllocation {
  authority: PublicKey;
  amount: BN;
}

export interface ISolanaAllocation {
  equal: BN | null;
  custom: ISolanaCustomAllocation[] | null;
}

export interface ISolanaReservedRights {
  totalReservedRights: BN;
  totalReservedPercentage: number;
  individualRights: ISolanaIndividualRights[];
}

export interface ISolanaIndividualRights {
  memberPubkey: PublicKey;
  rightPercentage: number;
  amountOfRights: BN;
}
export interface IClubCreationData {
  clubAddress: string;
  founderPubkey: string;
  clubConfig: string;
  updatedAt: Date;
  createdAt: Date;
}
